/**
 * Blackout mode — planets progressively go dark, restricting visibility.
 * Pure functions — no side effects, no rendering.
 */

import type { PlanetState, PlayerState, ModeConfig, ModeState, BlackoutState, WinResult } from '../types';
import { SeededRandom } from './random';
import {
  DEFAULT_BLACKOUT_RATE,
  DEFAULT_BLACKOUT_DELAY,
  DEFAULT_BLACKOUT_START_TURN,
} from '../constants';

// =============================================================================
// SCHEDULE
// =============================================================================

/**
 * Build the ordered list of planet indices that will go dark.
 * Stars and wormholes are excluded.
 */
export function buildBlackoutSchedule(
  planets: PlanetState[],
  config: ModeConfig,
  seed: string,
): number[] {
  const order = config.blackoutOrder ?? 'random';

  // Collect eligible planet indices (skip stars, wormholes)
  const eligible = planets
    .map((p, i) => ({ index: i, planet: p }))
    .filter(e => e.planet.type === 'planet' && e.planet.alive);

  switch (order) {
    case 'innerFirst': {
      // Sort by distance to origin (star is at/near origin typically)
      const starPos = planets.find(p => p.type === 'star')?.position ?? { x: 0, y: 0, z: 0 };
      eligible.sort((a, b) => {
        const da = distSq(a.planet.position, starPos);
        const db = distSq(b.planet.position, starPos);
        return da - db;
      });
      return eligible.map(e => e.index);
    }
    case 'outerFirst': {
      const starPos = planets.find(p => p.type === 'star')?.position ?? { x: 0, y: 0, z: 0 };
      eligible.sort((a, b) => {
        const da = distSq(a.planet.position, starPos);
        const db = distSq(b.planet.position, starPos);
        return db - da;
      });
      return eligible.map(e => e.index);
    }
    case 'random':
    default: {
      const rng = new SeededRandom(`${seed}-blackout-schedule`);
      const indices = eligible.map(e => e.index);
      // Fisher-Yates shuffle
      for (let i = indices.length - 1; i > 0; i--) {
        const j = rng.int(0, i);
        [indices[i], indices[j]] = [indices[j], indices[i]];
      }
      return indices;
    }
  }
}

// =============================================================================
// INIT
// =============================================================================

export function initBlackoutState(
  config: ModeConfig,
  planets: PlanetState[],
  seed: string,
): BlackoutState {
  const schedule = buildBlackoutSchedule(planets, config, seed);
  const startTurn = config.blackoutStartTurn ?? DEFAULT_BLACKOUT_START_TURN;
  return {
    darkenedPlanets: [],
    schedule,
    nextBlackoutTurn: startTurn,
  };
}

// =============================================================================
// TICK
// =============================================================================

export interface BlackoutTickResult {
  updatedState: BlackoutState;
  newlyDarkened: number[];
}

/**
 * Advance the blackout layer for the given turn.
 * Returns updated state and list of newly darkened planet indices.
 */
export function tickBlackout(
  state: BlackoutState,
  turnNumber: number,
  config: ModeConfig,
): BlackoutTickResult {
  const rate = config.blackoutRate ?? DEFAULT_BLACKOUT_RATE;

  if (turnNumber < state.nextBlackoutTurn || state.schedule.length === 0) {
    return { updatedState: { ...state }, newlyDarkened: [] };
  }

  // Darken `rate` planets from the schedule
  const toDarken = state.schedule.slice(0, rate);
  const remainingSchedule = state.schedule.slice(rate);

  const newlyDarkened = toDarken.filter(pi => !state.darkenedPlanets.includes(pi));
  const darkenedPlanets = [...state.darkenedPlanets, ...newlyDarkened];

  return {
    updatedState: {
      ...state,
      darkenedPlanets,
      schedule: remainingSchedule,
      nextBlackoutTurn: turnNumber + 1,
    },
    newlyDarkened,
  };
}

// =============================================================================
// WARNINGS
// =============================================================================

export interface BlackoutWarning {
  planetIndex: number;
  turnsUntil: number;
}

/**
 * Get warnings for planets about to go dark.
 */
export function getBlackoutWarnings(
  state: BlackoutState | undefined,
  turnNumber: number,
  config: ModeConfig,
): BlackoutWarning[] {
  if (!state) return [];

  const delay = config.blackoutDelay ?? DEFAULT_BLACKOUT_DELAY;
  const rate = config.blackoutRate ?? DEFAULT_BLACKOUT_RATE;
  const turnsUntilNext = state.nextBlackoutTurn - turnNumber;

  if (turnsUntilNext > delay || turnsUntilNext <= 0) return [];
  if (state.schedule.length === 0) return [];

  const upcoming = state.schedule.slice(0, rate);
  return upcoming.map(pi => ({ planetIndex: pi, turnsUntil: turnsUntilNext }));
}

// =============================================================================
// WIN CONDITIONS (standalone helpers; the match pipeline in modes.ts composes these)
// =============================================================================

/**
 * Check if a blackout match is over.
 * Over when: one team left, all teams dead, all planets dark, or turn limit exceeded.
 */
export function isBlackoutGameOver(
  players: PlayerState[],
  modeState: ModeState,
  turnLimit?: number,
  turnNumber?: number,
): boolean {
  const aliveTeams = new Set(players.filter(p => p.alive).map(p => p.teamIndex));
  if (aliveTeams.size <= 1) return true;

  if (modeState.blackout && modeState.blackout.schedule.length === 0) {
    // All planets that will darken have darkened
    return true;
  }

  if (turnLimit && turnNumber && turnNumber > turnLimit) return true;

  return false;
}

/**
 * Determine winner of blackout mode.
 */
export function checkBlackoutWin(
  players: PlayerState[],
  turnLimit?: number,
  turnNumber?: number,
): WinResult | null {
  const alivePlayers = players.filter(p => p.alive);
  if (alivePlayers.length === 0) return null;

  const aliveTeams = new Set(alivePlayers.map(p => p.teamIndex));

  // Elimination
  if (aliveTeams.size === 1) {
    const winningTeamIndex = alivePlayers[0].teamIndex;
    return {
      winningTeamIndex,
      winningPlayer: alivePlayers[0],
      reason: 'elimination',
    };
  }

  // Turn limit / all darkened: score by survivors > kills > damage
  if ((turnLimit && turnNumber && turnNumber > turnLimit) || aliveTeams.size > 1) {
    return scoreDeathmatchStyle(players, alivePlayers);
  }

  return null;
}

function scoreDeathmatchStyle(
  players: PlayerState[],
  alivePlayers: PlayerState[],
): WinResult | null {
  const teamStats = new Map<number, { alive: number; kills: number; damage: number }>();

  for (const p of players) {
    const stats = teamStats.get(p.teamIndex) ?? { alive: 0, kills: 0, damage: 0 };
    if (p.alive) stats.alive++;
    stats.kills += p.kills;
    stats.damage += p.damageDealt;
    teamStats.set(p.teamIndex, stats);
  }

  const entries = [...teamStats.entries()];
  entries.sort((a, b) => {
    if (b[1].alive !== a[1].alive) return b[1].alive - a[1].alive;
    if (b[1].kills !== a[1].kills) return b[1].kills - a[1].kills;
    return b[1].damage - a[1].damage;
  });

  if (entries.length >= 2) {
    const [first, second] = entries;
    if (first[1].alive === second[1].alive &&
        first[1].kills === second[1].kills &&
        first[1].damage === second[1].damage) {
      return null;
    }
  }

  const winningTeamIndex = entries[0][0];
  const winningPlayer = alivePlayers.find(p => p.teamIndex === winningTeamIndex)
    ?? players.find(p => p.teamIndex === winningTeamIndex)!;

  const teamScores: Record<number, number> = {};
  for (const [teamIdx, stats] of entries) {
    teamScores[teamIdx] = stats.alive * 1000 + stats.kills * 10 + Math.round(stats.damage);
  }

  return {
    winningTeamIndex,
    winningPlayer,
    reason: 'turnLimit',
    teamScores,
  };
}

// =============================================================================
// HELPERS
// =============================================================================

function distSq(a: { x: number; y: number; z: number }, b: { x: number; y: number; z: number }): number {
  const dx = a.x - b.x;
  const dy = a.y - b.y;
  const dz = a.z - b.z;
  return dx * dx + dy * dy + dz * dz;
}
